'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { ACCESSIBLE_APPS, rolesIncludeContractor, USER_ROLES } from '@workspace/ui/lib';
import { getContractorByUserId } from '@/actions/contractors';
import { useAuthStore } from '@/stores/authStore';
import type { Contractor } from '@/types/contractor';

/**
 * Reactive hook to check if user is a contractor (client-side only)
 * This will re-render components when the user's role changes
 */
export const useIsContractor = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error(
      'useIsContractor is for client-side only. Use isContractorServer for server-side.'
    );
  }

  return useAuthStore(
    (state) =>
      state.user?.accessible_app === ACCESSIBLE_APPS.CONTRACTOR ||
      rolesIncludeContractor(state.user?.roles)
  );
};

/**
 * Reactive hook to check if user is a contractor (client-side only)
 * This will re-render components when the user's role changes
 */
export const useIsGigWorker = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error(
      'useIsGigWorker is for client-side only. Use isGigWorkerServer for server-side.'
    );
  }

  return useAuthStore(
    (state) => state.user?.accessible_app === ACCESSIBLE_APPS.GIG_WORKER || false
  );
};

export const useIsIndividualCraft = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error(
      'useIsIndividualCraft is for client-side only. Use isIndividualCraftServer for server-side.'
    );
  }

  return useAuthStore(
    (state) => state.user?.accessible_app === ACCESSIBLE_APPS.INDIVIDUAL_CRAFT || false
  );
};

/**
 * Reactive hook to check if user is a B2C employee (client-side only)
 * This will re-render components when the user's role changes
 */
export const useIsB2CEmployee = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error(
      'useIsB2CEmployee is for client-side only. Use isB2CEmployeeServer for server-side.'
    );
  }

  return useAuthStore(
    (state) =>
      (state.user?.accessible_app === ACCESSIBLE_APPS.EMPLOYEE &&
        state.user?.roles?.includes(USER_ROLES.B2C_EMPLOYEE)) ||
      false
  );
};

/**
 * Reactive hook to check if user is B2C standard (client-side only)
 * This will re-render components when the user's role changes
 */
export const useIsB2CStandard = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error('useIsB2CStandard is for client-side only.');
  }

  return useAuthStore(
    (state) =>
      (state.user?.accessible_app === ACCESSIBLE_APPS.EMPLOYEE &&
        state.user?.roles?.includes(USER_ROLES.B2C_STANDARD)) ||
      false
  );
};

/**
 * Reactive hook to check if user is B2C free (client-side only)
 * This will re-render components when the user's role changes
 */
export const useIsB2CFree = (): boolean => {
  if (typeof window === 'undefined') {
    throw new Error('useIsB2CFree is for client-side only.');
  }

  return useAuthStore(
    (state) =>
      (state.user?.accessible_app === ACCESSIBLE_APPS.EMPLOYEE &&
        state.user?.roles?.includes(USER_ROLES.B2C_FREE)) ||
      false
  );
};

/**
 * Without an organisation id the backend returns the first engagement ordered by
 * name, so a user who belongs to several organisations can resolve to the wrong one.
 */
export function useContractorByUserId(
  userId: number,
  organisationId?: number,
  enabled: boolean = true
): UseQueryResult<Contractor, Error> {
  return useQuery({
    queryKey: ['contractor', userId, organisationId],
    queryFn: () => getContractorByUserId(userId, organisationId),
    enabled: enabled,
    staleTime: 600000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}
